import React, { useCallback, useContext } from "react";
import "./cases-content.component.scoped.scss";
import { useTranslation } from "react-i18next";
import { DataTableComponent } from "../../../../../components/table/data-table/data-table.component";
import { CasesContext, GlobalContext } from "../../../../../store";
import useFetchCases from "../../../../../store/hooks/useFetchCases";
import { CASES_PAGE_TYPES } from "../../../types/AI-roles";
import {
  Box,
  Button,
  debounce,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { UserRoles } from "../../../../../types/userRoles";
import { history } from "react-router-guard";
import { icons } from "../../../../../statics/icons";

interface CasesListProps {
  casesPageType: CASES_PAGE_TYPES;
}

// eslint-disable-next-line react/prop-types
const CasesContent: React.FC<CasesListProps> = ({ casesPageType }) => {
  const { t } = useTranslation();

  const {
    AIInventoryColumns,
    AIProductsColumns,
    AIDemandColumns,
    filter,
    setFilter,
    options,
    pagination,
    sort,
  } = useContext(CasesContext);
  const { user } = useContext(GlobalContext);

  const { loading, data } = useFetchCases(casesPageType, filter);

  const paginateTable = (pagination: any) => {
    const { items, currentPage } = pagination;
    const params = {
      ...filter,
      items,
      page: currentPage,
      order: options.sortDirection,
      sort: options.sortColumn,
    };
    setFilter(params);
  };

  const sortTable = (fieldName: string, sortDirection: string) => {
    const params = { fieldName, sortDirection };
    const filterObj = {
      ...filter,
      page: 1,
      items: pagination.items,
      sort: fieldName,
      order: sortDirection,
    };
    sort(params);
    setFilter(filterObj);
  };

  const onTextSearch = useCallback(
    debounce((query) => {
      filterChange({ ...filter, searchText: query });
    }, 600),
    [filter],
  );

  const filterChange = (params: any) => {
    sort({
      fieldName: "",
      sortDirection: "",
    });
    const filterObj = {
      ...params,
      items: pagination.items,
      page: 1,
    };
    setFilter(filterObj);
  };

  const exportData = async () => {
    for (const row of data) {
      if (row.checked) {
        // await downloadPDF(row);
      }
    }
  };

  const createUseCase = () => {
    history.push(`/ai?role=${casesPageType}`);
  };

  // Helper function to check if button should be visible based on page type and user role
  const canShowCreateButton = () => {
    // Hide button for PUBLISHED page type
    if (casesPageType === CASES_PAGE_TYPES.PUBLISHED) {
      return false;
    }

    // For DEVELOPMENT page type: only SUPER_ADMIN, COORDINATOR, or EDITOR
    if (casesPageType === CASES_PAGE_TYPES.DEVELPMENT) {
      return (
        user?.role === UserRoles.SUPER_ADMIN ||
        user?.role === UserRoles.COORDINATOR ||
        user?.role === UserRoles.EDITOR
      );
    }

    // For DEMAND page type: SUPER_ADMIN, COORDINATOR, EDITOR, or CONTRIBUTOR
    if (casesPageType === CASES_PAGE_TYPES.DEMAND) {
      return (
        user?.role === UserRoles.SUPER_ADMIN ||
        user?.role === UserRoles.COORDINATOR ||
        user?.role === UserRoles.EDITOR ||
        user?.role === UserRoles.CONTRIBUTOR
      );
    }

    // Default: hide button for other page types
    return false;
  };

  return (
    <div className="cases-content">
      <Box className="d-flex align-items-center mb-4">
        <Typography variant="body2">
          <span
            style={{ fontSize: "18px", fontWeight: 500, marginRight: "1rem" }}
          >
            {casesPageType === "PUBLISHED"
              ? t("AI_USE_CASES")
              : t("AI_DEMAND_MANAGEMENT")}
          </span>
        </Typography>
      </Box>

      <Box className="cases_text_input mb-4" display="flex">
        <TextField
          variant="standard"
          type="text"
          autoComplete="off"
          onChange={(e: any) => onTextSearch(e.target.value)}
          placeholder={t("FIND_AI_PROJECT")}
          InputProps={{
            style: {
              flexDirection: "row-reverse",
              padding: 0,
            },
            endAdornment: (
              <InputAdornment position="start">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {canShowCreateButton() && (
        <Box className="mb-4 text-center">
          <Button
            className="btn-primary-theme"
            variant="contained"
            size="medium"
            color="primary"
            onClick={createUseCase}
          >
            {t("PAGES_HEAD.CREATE_NEW_WITH_AI_ASSISTANT")}
          </Button>

          <Box className="assistant_bref">
            <img src={icons.ASSISTANT} className="mt-2" alt="" />
            <Box className="info">
              <Typography variant="subtitle2" style={{ color: "#fafafa" }}>
                Try AI-Assistent Lisa Mona
              </Typography>
              <Typography variant="body2" style={{ color: "#979797" }}>
                AI ask questions or give prompts, and I will respond
                accordingly.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <div className="table-section">
        <DataTableComponent
          tableName="cases"
          loading={loading}
          columns={
            casesPageType === "PUBLISHED"
              ? AIProductsColumns
              : casesPageType === "DEVELOPMENT"
              ? AIInventoryColumns
              : AIDemandColumns
          }
          casesPageType={casesPageType}
          data={data}
          options={options}
          sort={sortTable}
          pagination={pagination}
          exportData={true}
          onExport={exportData}
          paginate={paginateTable}
          onFilterChange={filterChange}
          filter={filter}
          title={
            casesPageType === "PUBLISHED"
              ? t("LIST_OF_AI_USE_CASES") +
                ` (${pagination.total} ${t("RESULTS")})`
              : casesPageType === "DEVELOPMENT"
              ? t("AIInventoryColumns")
              : t("LIST_OF_REQUESTED_AI_USE_CASES") +
                ` (${pagination.total} ${t("RESULTS")})`
          }
          subTitle=""
          clickableRow={["case", "id"]}
        />
      </div>
    </div>
  );
};

export default CasesContent;
